import { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { LocalDate } from '@js-joda/core';

interface CancelPlanModalProps {
    show: boolean;
    onHide: () => void;
    onConfirmCancel: (reason: string, cancellationDate: string) => void;
    /** When true, access ends at end of day instead of end of billing period. */
    isComplimentaryPlan?: boolean;
    /** Plain group or enhanced group — cancel deactivates all parkers on the account. */
    isCalculatedGroup?: boolean;
    minCancellationDate?: string;
    defaultCancellationDate?: string;
}

export default function UserPlanCancellationModal({
    show,
    onHide,
    onConfirmCancel,
    isComplimentaryPlan = false,
    isCalculatedGroup = false,
    minCancellationDate,
    defaultCancellationDate,
}: CancelPlanModalProps) {
    const [reason, setReason] = useState('');
    const minDate = minCancellationDate ?? LocalDate.now().toString();
    const defaultDate = defaultCancellationDate ?? minDate;
    const [cancellationDate, setCancellationDate] = useState(defaultDate);
    const isValid = reason.trim().length >= 3;
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (show) {
            setCancellationDate(defaultDate);
            const id = setTimeout(() => inputRef.current?.focus(), 0);
            return () => clearTimeout(id);
        }
    }, [show, defaultDate]);

    function handleHide() {
        setReason('');
        onHide();
    }

    function handleConfirm() {
        if (!isValid) return;
        onConfirmCancel(reason.trim(), cancellationDate);
        setReason('');
    }

    return (
        <Modal centered show={show} onHide={handleHide}>
            <Modal.Header closeButton>
                <Modal.Title>Cancel Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-3">Are you sure you want to cancel the plan?</p>
                {isComplimentaryPlan ? (
                    <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
                        <InfoCircleFill className="flex-shrink-0 mt-1" />
                        <span>
                            {isCalculatedGroup
                                ? "Access will be revoked at the end of the day. All parkers in the account will be deactivated."
                                : "Access will be revoked at the end of the day."}
                        </span>
                    </div>
                ) : (
                    <>
                        <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
                            <InfoCircleFill className="flex-shrink-0 mt-1" />
                            <span>
                                {isCalculatedGroup ? (
                                    <>
                                        Access continues through the end of the subscription period.
                                        All parkers in the account will be deactivated.
                                        Any applicable credits must be issued manually.
                                    </>
                                ) : (
                                    <>
                                        Access continues through the end of the subscription period.
                                        Any applicable credits must be issued manually.
                                    </>
                                )}
                            </span>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label>Cancellation date</Form.Label>
                            <Form.Control
                                type="date"
                                min={minDate}
                                value={cancellationDate}
                                onChange={(e) => setCancellationDate(e.target.value)}
                            />
                        </Form.Group>
                    </>
                )}
                <Form.Group className="mb-3">
                    <Form.Label>Reason for cancellation <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        ref={inputRef}
                        as="textarea"
                        rows={3}
                        placeholder="Enter reason for cancellation..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        isInvalid={reason.length > 0 && !isValid}
                    />
                    <Form.Control.Feedback type="invalid">
                        Please enter at least 3 characters.
                    </Form.Control.Feedback>
                </Form.Group>
                <div className="d-flex flex-column gap-3">
                    <Button variant="danger" onClick={handleConfirm} disabled={!isValid}>
                        Confirm Cancellation
                    </Button>
                    <Button variant="outline-secondary" onClick={handleHide}>
                        Go Back
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
